import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, CreditCard, Search, TrendingUp, Wallet } from 'lucide-react';
import { AdminMetricCard } from '@/components/admin/AdminMetricCard';
import { AdminTransactionTable } from '@/components/admin/AdminTransactionTable';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminTransactionService, getApiErrorMessage } from '@/services';
import type { TransactionCategory, TransactionItem, TransactionStatus } from '@/types/transactions';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { formatTransactionNaira } from '@/utils/transactionFormatters';
import { notify } from '@/utils/notify';

function adminTransactionDetailPath(id: number) {
  return `/admin/transactions/${id}`;
}

export function AdminTransactionsPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [stats, setStats] = useState({
    totalCount: 0,
    debitVolume: 0,
    totalProfit: 0,
    successCount: 0,
    failedCount: 0,
    pendingCount: 0,
  });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<TransactionCategory | ''>('');
  const [status, setStatus] = useState<TransactionStatus | ''>('');
  const [period, setPeriod] = useState<'7d' | '30d' | 'all'>('30d');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        adminTransactionService.list({
          page: pagination.page,
          limit: pagination.limit,
          search: search || undefined,
          category: category || undefined,
          status: status || undefined,
          period,
        }),
        adminTransactionService.getStats(period),
      ]);
      setTransactions(listRes.data?.transactions ?? []);
      setPagination(listRes.data?.pagination ?? pagination);
      const tx = statsRes.data?.transactions;
      if (tx) {
        setStats({
          totalCount: tx.totalCount,
          debitVolume: tx.debitVolume,
          totalProfit: tx.totalProfit,
          successCount: tx.successCount,
          failedCount: tx.failedCount,
          pendingCount: tx.pendingCount,
        });
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load transactions'));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, category, status, period]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPagination((p) => ({ ...p, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filterSelectClass = cn('rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2', shell.select);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold sm:text-2xl">Transactions</h1>
        <p className={cn('text-sm', shell.cardMuted)}>Platform ledger with profit and user context</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminMetricCard
          theme={theme}
          title="Transactions"
          value={stats.totalCount.toLocaleString('en-NG')}
          subtitleLines={[
            `${stats.successCount} successful · ${stats.pendingCount} pending`,
            `${stats.failedCount} failed`,
          ]}
          icon={CreditCard}
          iconBg="bg-amber-500"
        />
        <AdminMetricCard
          theme={theme}
          title="Debit volume"
          value={formatTransactionNaira(stats.debitVolume)}
          icon={TrendingUp}
          iconBg="bg-orange-500"
          valueSize="md"
        />
        <AdminMetricCard
          theme={theme}
          title="Profit"
          value={formatTransactionNaira(stats.totalProfit)}
          icon={Wallet}
          iconBg="bg-emerald-500"
          valueSize="md"
        />
        <AdminMetricCard
          theme={theme}
          title="Successful"
          value={stats.successCount.toLocaleString('en-NG')}
          icon={CheckCircle2}
          iconBg="bg-teal-500"
        />
      </div>

      <Card className={cn('space-y-4 p-4 sm:p-5', shell.card)}>
        <div className="relative">
          <Search className={cn('absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', shell.cardMuted)} />
          <input
            className={cn('w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2', shell.input)}
            placeholder="Search reference, service, user..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <select
            className={filterSelectClass}
            value={category}
            onChange={(e) => {
              setCategory(e.target.value as TransactionCategory | '');
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <option value="">All categories</option>
            <option value="wallet">Wallet</option>
            <option value="airtime">Airtime</option>
            <option value="data">Data</option>
            <option value="bills">Bills</option>
            <option value="verification">Verification</option>
          </select>
          <select
            className={filterSelectClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as TransactionStatus | '');
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <option value="">All statuses</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          <select
            className={filterSelectClass}
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value as typeof period);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </Card>

      {loading ? (
        <Loader label="Loading transactions..." />
      ) : transactions.length === 0 ? (
        <Card className={cn('p-8 text-center text-sm', shell.card, shell.cardMuted)}>
          No transactions match your filters.
        </Card>
      ) : (
        <AdminTransactionTable
          transactions={transactions}
          theme={theme}
          detailPath={adminTransactionDetailPath}
        />
      )}

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between gap-3">
          <p className={cn('text-sm', shell.cardMuted)}>
            Page {pagination.page} of {pagination.totalPages} · {pagination.total.toLocaleString('en-NG')} total
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              className={cn('rounded-xl border px-3 py-1.5 text-sm disabled:opacity-40', shell.outlineBtn)}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className={cn('rounded-xl border px-3 py-1.5 text-sm disabled:opacity-40', shell.outlineBtn)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
